"use client";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";

// Color palette based on #395750 with Warm Beige/Cream
const colors = {
  primary: "#395750",      // Your company color - Teal
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  cream: "#F5F0E1",        // Warm cream
  beige: "#E8DCC8",        // Soft beige
  beigeDark: "#D4C4A8",    // Darker beige
  textWarm: "#4A3B2C",     // Warm brown for text
  white: "#FFFFFF",
  accent: "#5B8A7E",       // Accent teal
};

const HeroSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const { scrollY } = useScroll();

  // Parallax effect - content moves up slightly on scroll
  const yTransform = useTransform(scrollY, [0, 500], [0, 100]);
  const opacityTransform = useTransform(scrollY, [0, 300], [1, 0]);

  // Handle visibility to prevent animation conflicts
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setIsVisible(rect.bottom > 0);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Generate random positions once on mount to prevent hydration mismatch
  const [circles] = useState(() => 
    Array.from({ length: 15 }).map(() => ({
      width: Math.random() * 40 + 20,
      height: Math.random() * 40 + 20,
      top: Math.random() * 100,
      left: Math.random() * 100,
      duration: Math.random() * 4 + 4,
      delay: Math.random() * 2,
    }))
  );

  return (
    <div 
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
        position: "relative",
        zIndex: 1,
      }}
    >
      <motion.div
        style={{
          y: yTransform,
          opacity: opacityTransform,
        }}
        className="container mx-auto px-4 py-24 pt-32 text-center"
      >
        {/* Animated Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <div 
            className="mb-4 inline-block rounded-full px-6 py-2"
            style={{ 
              backgroundColor: `${colors.cream}20`,
              backdropFilter: "blur(10px)"
            }}
          >
            <span className="font-medium" style={{ color: colors.cream }}>
              Yegna For All!
            </span>
          </div>
        </motion.div>

        {/* Main Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 text-4xl font-bold sm:text-5xl md:text-6xl"
          style={{ color: colors.white }}
        >
          Empowering Communities
          <br />
          <span style={{ color: colors.cream }}>Together</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mx-auto max-w-2xl text-xl"
          style={{ color: `${colors.white}E6` }}
        >
          Creating sustainable economic growth through accessible microfinance 
          solutions and community partnerships
        </motion.p>

        {/* Animated Symbol */}
        <motion.div
          initial={{ scale: 0 }}
          animate={isVisible ? { scale: 1 } : { scale: 0 }}
          transition={{ delay: 0.6, duration: 0.5, type: "spring" }}
          className="mt-12 flex justify-center"
        >
          <div 
            className="flex h-24 w-24 items-center justify-center rounded-full text-4xl backdrop-blur-sm"
            style={{ 
              backgroundColor: `${colors.white}10`,
              backdropFilter: "blur(10px)"
            }}
          >
            🤝
          </div>
        </motion.div>
      </motion.div>

      {/* Wave Divider - Fixed positioning */}
      <div className="absolute bottom-0 left-0 right-0 w-full pointer-events-none" style={{ height: "64px" }}>
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="absolute bottom-0 w-full h-full"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            opacity=".25"
            fill={colors.cream}
          />
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            opacity=".5"
            fill={colors.beige}
          />
          <path
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
            fill={colors.cream}
          />
        </svg>
      </div>

      {/* Floating Circles Background - Optimized with will-change */}
      {circles.map((circle, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: circle.width,
            height: circle.height,
            top: `${circle.top}%`,
            left: `${circle.left}%`,
            backgroundColor: `${colors.cream}15`,
            willChange: "transform, opacity",
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0, 0.3, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: circle.duration,
            delay: circle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

export default HeroSection;